import React, { useState, useRef, useEffect } from 'react';
import { snapToNearest } from '../utils/snapMagnitude';
import { formatRelativeTime } from '../utils/formatTime';
import EarthquakeDropdown from './EarthquakeDropdown';

function isCoordValid(str) {
  return /^-?\d*\.?\d{0,4}$/.test(str);
}

function makeCoordKeyHandler(onEnter) {
  return e => {
    if (e.key === 'Enter') { onEnter(); return; }
    const { key, target: { value, selectionStart, selectionEnd } } = e;
    if (['Backspace', 'Delete', 'Tab', 'Escape',
         'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
         'Home', 'End'].includes(key)) return;
    if (e.ctrlKey || e.metaKey) return;
    const simulated = value.slice(0, selectionStart) + key + value.slice(selectionEnd);
    if (isCoordValid(simulated)) return;
    e.preventDefault();
  };
}

// Vertical "Define Earthquake" form: Magnitude/Latitude/Longitude fields +
// Recent Earthquakes lookup. Content-only — the caller (ControlPanel)
// decides whether this renders in a persistent sidebar, a slide-in drawer,
// or a bottom sheet. onAfterAction fires after a successful search or a
// "Use" pick, so ControlPanel can auto-close the drawer/sheet on tablet/mobile.
export default function EarthquakeControls({ magnitude, onMagnitudeSelect, searchCoords, onSearch, onAfterAction }) {
  const [magInput, setMagInput] = useState(magnitude != null ? magnitude.toFixed(1) : '');
  const [magWarning, setMagWarning] = useState(null);
  const magWarnTimer = useRef(null);

  const [inputLat, setInputLat] = useState('');
  const [inputLng, setInputLng] = useState('');
  const [latError, setLatError] = useState(null);
  const [lngError, setLngError] = useState(null);

  const [noMagAlert, setNoMagAlert] = useState(false);
  const noMagTimer = useRef(null);
  const magnitudeRef = useRef(magnitude);
  magnitudeRef.current = magnitude;

  // Earthquake dropdown state
  const [eqOpen, setEqOpen] = useState(false);
  const [eqKey, setEqKey] = useState(0);
  const eqBtnRef = useRef(null);

  // Fill inputs from map click; show alert if Mw is empty
  useEffect(() => {
    if (!searchCoords) return;
    setInputLat(searchCoords.lat.toFixed(4));
    setInputLng(searchCoords.lng.toFixed(4));
    setLatError(null);
    setLngError(null);
    if (!magnitudeRef.current) {
      setNoMagAlert(true);
      if (noMagTimer.current) clearTimeout(noMagTimer.current);
      noMagTimer.current = setTimeout(() => setNoMagAlert(false), 4000);
    } else {
      setNoMagAlert(false);
    }
  }, [searchCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleMagKeyDown(e) {
    const { key, target: { value, selectionStart, selectionEnd } } = e;
    if (key === 'Enter') { const snapped = commitMag(value); handleSearch(snapped); return; }
    if (['Backspace', 'Delete', 'Tab', 'Escape',
         'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
         'Home', 'End'].includes(key)) return;
    if (e.ctrlKey || e.metaKey) return;
    const simulated = value.slice(0, selectionStart) + key + value.slice(selectionEnd);
    if (/^\d*\.?\d?$/.test(simulated)) return;
    e.preventDefault();
  }

  function commitMag(raw) {
    const num = parseFloat(raw);
    if (!raw.trim() || isNaN(num)) return null;
    const snapped = snapToNearest(num);
    setMagInput(snapped.toFixed(1));
    setNoMagAlert(false);
    if (Math.abs(num - snapped) > 1e-9) {
      if (magWarnTimer.current) clearTimeout(magWarnTimer.current);
      setMagWarning(`M ${num.toFixed(1)} not available — snapped to M ${snapped.toFixed(1)}`);
      magWarnTimer.current = setTimeout(() => setMagWarning(null), 10000);
    } else {
      setMagWarning(null);
    }
    onMagnitudeSelect(snapped);
    return snapped;
  }

  function handleSearch(forceMag) {
    let effectiveMag = forceMag ?? magnitude;
    if (!effectiveMag && magInput.trim()) {
      effectiveMag = commitMag(magInput);
    }
    if (!effectiveMag) return;

    let ok = true;
    const lat = parseFloat(inputLat);
    const lng = parseFloat(inputLng);

    if (!inputLat.trim() || isNaN(lat)) {
      setLatError('Latitude must be between -90 and 90'); ok = false;
    } else if (lat < -90 || lat > 90) {
      setLatError('Latitude must be between -90 and 90'); ok = false;
    } else {
      setLatError(null);
    }

    if (!inputLng.trim() || isNaN(lng)) {
      setLngError('Longitude must be between -180 and 180'); ok = false;
    } else if (lng < -180 || lng > 180) {
      setLngError('Longitude must be between -180 and 180'); ok = false;
    } else {
      setLngError(null);
    }

    if (!ok) return;
    onSearch(lat, lng, { magnitude: effectiveMag });
    onAfterAction?.();
  }

  // "Use" button in the earthquake dropdown
  function handleUseEarthquake(eq) {
    const snapped = snapToNearest(eq.magnitude);

    setMagInput(snapped.toFixed(1));
    setInputLat(eq.lat.toFixed(4));
    setInputLng(eq.lon.toFixed(4));
    setLatError(null);
    setLngError(null);

    if (Math.abs(eq.magnitude - snapped) > 1e-9) {
      if (magWarnTimer.current) clearTimeout(magWarnTimer.current);
      setMagWarning(`M ${eq.magnitude.toFixed(1)} not available — snapped to M ${snapped.toFixed(1)}`);
      magWarnTimer.current = setTimeout(() => setMagWarning(null), 4000);
    }

    const popup =
      `<b>M ${eq.magnitude.toFixed(1)}</b> · ${eq.place}` +
      `<br><span style="color:#888;font-size:11px">${formatRelativeTime(eq.time)}</span>`;

    onSearch(eq.lat, eq.lon, { magnitude: snapped, popup, place: eq.place });
    onAfterAction?.();
  }

  function handleEqButtonClick() {
    if (!eqOpen) {
      setEqOpen(true);
    } else {
      setEqKey(k => k + 1);
    }
  }

  const canSearch    = magnitude !== null && inputLat.trim() !== '' && inputLng.trim() !== '';
  const coordsFilled = inputLat.trim() !== '' && inputLng.trim() !== '';
  const showGuidance = magnitude !== null && !coordsFilled && !noMagAlert;
  const coordKeyDown = makeCoordKeyHandler(handleSearch);

  return (
    <div className="eqc">
      <div className="eqc-section-label">Define Earthquake</div>

      <div className={`eqc-field${noMagAlert ? ' eqc-field-alert' : ''}`}>
        <label className="eqc-label">Magnitude</label>
        <input
          className="eqc-input"
          type="text"
          inputMode="decimal"
          placeholder="e.g. 7.8–9.4"
          value={magInput}
          onChange={e => { const v = e.target.value; if (/^\d*\.?\d?$/.test(v)) setMagInput(v); }}
          onKeyDown={handleMagKeyDown}
          onBlur={e => commitMag(e.target.value)}
        />
      </div>

      <div className="eqc-row">
        <div className="eqc-field">
          <label className="eqc-label">Latitude</label>
          <input
            className={`eqc-input${latError ? ' eqc-input-error' : ''}`}
            type="text"
            inputMode="decimal"
            placeholder="13.5000"
            title={latError || ''}
            value={inputLat}
            onChange={e => { const v = e.target.value; if (isCoordValid(v)) setInputLat(v); }}
            onKeyDown={coordKeyDown}
          />
        </div>
        <div className="eqc-field">
          <label className="eqc-label">Longitude</label>
          <input
            className={`eqc-input${lngError ? ' eqc-input-error' : ''}`}
            type="text"
            inputMode="decimal"
            placeholder="100.5000"
            title={lngError || ''}
            value={inputLng}
            onChange={e => { const v = e.target.value; if (isCoordValid(v)) setInputLng(v); }}
            onKeyDown={coordKeyDown}
          />
        </div>
      </div>

      <button
        className="eqc-search-btn"
        onClick={() => handleSearch()}
        disabled={!canSearch}
        title={!canSearch ? 'Enter magnitude and coordinates first' : 'Find nearest scenarios'}
      >
        Define Earthquake
      </button>

      <button
        ref={eqBtnRef}
        className={`eqc-recent-btn${eqOpen ? ' active' : ''}`}
        onClick={handleEqButtonClick}
        title={eqOpen ? 'Click to refresh' : 'Show recent earthquakes near this region'}
      >
        <span className="eq-pulse-dot" />
        Recent earthquakes
      </button>

      {showGuidance && (
        <p className="eqc-hint">
          Click on the map or enter coordinates to search for nearby scenarios
        </p>
      )}
      {noMagAlert && (
        <p className="eqc-alert">Please enter a magnitude first</p>
      )}
      {magWarning && (
        <p className="eqc-warning">{magWarning}</p>
      )}

      {eqOpen && (
        <EarthquakeDropdown
          key={eqKey}
          btnRef={eqBtnRef}
          onClose={() => setEqOpen(false)}
          onUse={handleUseEarthquake}
        />
      )}
    </div>
  );
}
